import { Player } from '../entities/Player';
import { PowerUp } from '../entities/PowerUp';
import { AIBot } from '../entities/AIBot';
import { DEBUG_CONFIG } from '../config/debug';

export class UIManager {
  private gameArea: HTMLElement;
  private lobby: HTMLElement;
  private gameOver: HTMLElement;
  private playerList: HTMLElement;
  private debugPanel: HTMLElement | null = null;

  constructor() {
    this.gameArea = document.getElementById('gameArea')!;
    this.lobby = document.getElementById('lobby')!;
    this.gameOver = document.getElementById('gameOver')!;
    this.playerList = document.getElementById('playerList')!;
    
    if (DEBUG_CONFIG.enabled) {
      this.setupDebugPanel();
    }
  }

  showLobby(): void {
    this.lobby.style.display = 'block';
    this.gameOver.style.display = 'none';
  }

  hideLobby(): void {
    this.lobby.style.display = 'none';
  }

  showGameOver(winnerName: string | null): void {
    const winnerElement = document.getElementById('winner')!;
    if (winnerName) {
      winnerElement.textContent = `🏆 ${winnerName} vyhrál!`;
    } else {
      winnerElement.textContent = '⏰ Čas vypršel!';
    }
    this.gameOver.style.display = 'block';
  }

  hideGameOver(): void {
    this.gameOver.style.display = 'none';
  }

  updatePlayerList(players: Player[]): void {
    this.playerList.innerHTML = '';
    
    players.forEach(player => {
      const item = document.createElement('div');
      item.className = 'player-item';
      item.innerHTML = `
        <div class="player-color" style="background: ${player.color}"></div>
        <span>${player.name}</span>
      `;
      this.playerList.appendChild(item);
    });
  }

  spawnPlayerElement(player: Player): void {
    const bop = document.createElement('div');
    bop.className = 'bop';
    bop.style.width = player.radius * 2 + 'px';
    bop.style.height = player.radius * 2 + 'px';
    bop.style.background = player.color;
    bop.style.left = (player.position.x - player.radius) + 'px';
    bop.style.top = (player.position.y - player.radius) + 'px';
    bop.textContent = player.name.charAt(0);
    bop.dataset.playerId = player.id;
    
    // Add AI indicator
    if (player instanceof AIBot) {
      bop.classList.add('ai-bot');
      bop.title = `AI Bot (${player.personality})`;
    }
    
    const arrow = document.createElement('div');
    arrow.className = 'velocity-arrow';
    arrow.style.left = player.position.x + 'px';
    arrow.style.top = player.position.y + 'px';
    arrow.textContent = '→';
    arrow.style.display = 'none';
    
    player.element = bop;
    player.arrowElement = arrow;
    this.gameArea.appendChild(bop);
    this.gameArea.appendChild(arrow);
  }

  spawnPowerupElement(powerup: PowerUp): void {
    const element = powerup.createDOMElement();
    this.gameArea.appendChild(element);
  }

  clearGameArea(): void {
    this.gameArea.innerHTML = '';
  }

  updateGameStats(gameTime: number, alivePlayers: number, score: number): void {
    const gameTimeElement = document.getElementById('gameTime')!;
    const playersAliveElement = document.getElementById('playersAlive')!;
    const scoreElement = document.getElementById('scoreValue')!;
    
    gameTimeElement.textContent = Math.max(0, Math.ceil(gameTime)).toString();
    playersAliveElement.textContent = alivePlayers.toString();
    scoreElement.textContent = score.toString();
  }

  showToast(message: string, duration: number = 3000): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, duration);
  }

  getGameAreaDimensions(): { width: number; height: number } {
    return {
      width: this.gameArea.clientWidth,
      height: this.gameArea.clientHeight
    };
  }

  getTouchPosition(event: TouchEvent | MouseEvent): { x: number; y: number } {
    const rect = this.gameArea.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  setupEventListeners(
    onTouch: (position: { x: number; y: number }) => void,
    onResize: () => void
  ): void {
    this.gameArea.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const position = this.getTouchPosition(e);
      onTouch(position);
    }, { passive: false });
    
    this.gameArea.addEventListener('click', (e) => {
      const position = this.getTouchPosition(e);
      onTouch(position);
    });
    
    document.addEventListener('touchmove', (e) => {
      if (e.target && (e.target as Element).closest('#gameArea')) {
        e.preventDefault();
      }
    }, { passive: false });
    
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', () => {
      setTimeout(onResize, 100);
    });
  }

  positionPlayersInArea(players: Player[]): void {
    const dimensions = this.getGameAreaDimensions();
    
    players.forEach((player, index) => {
      player.alive = true;
      player.position.x = (dimensions.width / (players.length + 1)) * (index + 1);
      player.position.y = dimensions.height / 2;
      player.velocity = { vx: 0, vy: 0 };
    });
  }

  vibrate(pattern: number | number[] = 30): void {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  }

  checkiOS(): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      setTimeout(() => {
        this.showToast('📱 Pro přidání na plochu: Safari → Sdílet → Přidat na plochu');
      }, 2000);
    }
  }

  // Debug mode methods
  private setupDebugPanel(): void {
    this.debugPanel = document.createElement('div');
    this.debugPanel.id = 'debugPanel';
    this.debugPanel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      width: 300px;
      max-height: 400px;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 15px;
      border-radius: 10px;
      font-family: monospace;
      font-size: 12px;
      z-index: 10000;
      overflow-y: auto;
      border: 2px solid #00ff00;
    `;
    
    this.debugPanel.innerHTML = `
      <h3 style="color: #00ff00; margin-top: 0;">🐛 Debug Panel</h3>
      <div id="debugContent">Debug mode enabled</div>
      <button id="toggleDebug" style="
        background: #ff4444;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 5px;
        cursor: pointer;
        margin-top: 10px;
      ">Disable Debug</button>
    `;
    
    document.body.appendChild(this.debugPanel);
    
    // Add keyboard shortcut
    if (DEBUG_CONFIG.enableDebugShortcut) {
      document.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'D') {
          this.toggleDebugPanel();
        }
      });
    }
  }

  private toggleDebugPanel(): void {
    if (this.debugPanel) {
      this.debugPanel.style.display = this.debugPanel.style.display === 'none' ? 'block' : 'none';
    }
  }

  updateDebugInfo(debugInfo: any): void {
    if (!this.debugPanel || !debugInfo) return;
    
    const content = document.getElementById('debugContent');
    if (!content) return;
    
    content.innerHTML = `
      <div><strong>Game State:</strong> ${debugInfo.gameState}</div>
      <div><strong>Time:</strong> ${Math.ceil(debugInfo.gameTime)}s</div>
      <div><strong>Phase:</strong> ${debugInfo.currentPhase || 'None'}</div>
      <div><strong>Players:</strong> ${debugInfo.playersCount} (${debugInfo.alivePlayers} alive)</div>
      <div><strong>Powerups:</strong> ${debugInfo.powerupsCount}</div>
      <br>
      <div><strong>Players:</strong></div>
      ${debugInfo.players.map((p: any) => `
        <div style="margin-left: 10px; color: ${p.alive ? '#00ff00' : '#ff4444'}">
          ${p.name} ${p.isAI ? `(AI-${p.personality})` : '(Human)'}
          <br>HP: ${p.health}/100, Score: ${p.score}
          <br>Pos: (${Math.round(p.position.x)}, ${Math.round(p.position.y)})
          <br>Vel: (${p.velocity.vx.toFixed(1)}, ${p.velocity.vy.toFixed(1)})
        </div>
      `).join('')}
      <br>
      <div><strong>Powerups:</strong></div>
      ${debugInfo.powerups.map((p: any) => `
        <div style="margin-left: 10px; color: #ffff00">
          ${p.type} (${p.rarity}) at (${Math.round(p.position.x)}, ${Math.round(p.position.y)})
        </div>
      `).join('')}
    `;
  }

  showDebugToast(message: string): void {
    if (DEBUG_CONFIG.enabled) {
      this.showToast(`🐛 ${message}`);
    }
  }
}